import React from 'react'
import { Region, XP_REQUEST_MODE, type ComponentProps, type LayoutData } from '@enonic/react-components'
import ReferenceListPrototype from '/react4xp/components/prototype/ReferenceList'
import type { CardProps } from '/react4xp/components/prototype/Card/types'
import type { SubpageHeroFilter } from '/react4xp/components/prototype/SubpageHero/types'

/**
 * Reference List layout (I99X-382), wrapping the styled prototype component.
 *
 * The prototype drives filtering and paging through `onChange`/`onPageChange`
 * callbacks, but the processor derives its result from the query string
 * (`?industry=&offering=&location=&page=`). So the handlers here do not hold
 * React state - they rewrite the URL and let the server re-run the query. That
 * keeps a filtered listing linkable, shareable and back-button-friendly, and it
 * is the only option that works with a server-side processor.
 */
type FilterOption = {
	value: string
	label: string
}

type ReferenceCard = {
	href: string | null
	title: string
	clientName?: string
	intro?: string
	image?: { src: string; alt?: string }
}

type Pagination = {
	currentPage: number
	totalPages: number
	pageSize: number
	totalItems: number
	hasPrevious: boolean
	hasNext: boolean
}

type ReferenceListStrings = {
	industryFilter: string
	offeringFilter: string
	locationFilter: string
	filterAll: string
	emptyMessage: string
}

type ReferenceListLayoutData = {
	overline?: string
	heading?: string
	intro?: string
	items?: ReferenceCard[]
	filters?: { industries: FilterOption[]; offerings: FilterOption[]; locations: FilterOption[] }
	selectedFilters?: { industry: string | null; offering: string | null; location: string | null }
	strings?: ReferenceListStrings
	pagination?: Pagination
	error?: string
}

// Sentinel for the "no filter" choice. SelectButton has no empty state - it always
// shows one of its options - so the unfiltered case needs an option of its own.
// Taxonomy values are content ids, so this can never collide with a real one.
const ALL_VALUE = 'all'

// References are always internal content (the processor resolves `href` with
// `myPageUrl`), so no card is ever an external link. The whole Card is one link,
// with no non-linked variant, so an unresolvable href drops the card rather than
// rendering an <a> pointing at the current page.
function toCard(item: ReferenceCard): Omit<CardProps, 'titleSize'> | null {
	if (!item.href) return null

	return {
		image: item.image && { src: item.image.src, alt: item.image.alt || item.title },
		label: item.clientName,
		title: item.title,
		url: item.href,
		isExternal: false,
	}
}

export const ReferenceList = ({ component, data, meta }: ComponentProps<LayoutData>) => {
	const {
		overline,
		heading,
		items = [],
		filters,
		selectedFilters,
		strings,
		pagination,
	} = (data || {}) as ReferenceListLayoutData

	// Content Studio renders the layout inside its own frame; navigating there would
	// reload the editor rather than the page, so filtering/paging is inert in edit mode.
	const isEditMode = meta?.mode === XP_REQUEST_MODE.EDIT

	function navigate(params: Record<string, string | null>) {
		if (isEditMode || typeof window === 'undefined') return

		const url = new URL(window.location.href)
		Object.keys(params).forEach((key) => {
			const value = params[key]
			if (value) {
				url.searchParams.set(key, value)
			} else {
				url.searchParams.delete(key)
			}
		})
		window.location.assign(url.toString())
	}

	// Any filter change sends the reader back to page 1 - keeping the old page number
	// would land them past the end of a smaller result set.
	function buildFilter(id: string, label: string, options: FilterOption[], selected: string | null): SubpageHeroFilter {
		return {
			id,
			label,
			options: [{ value: ALL_VALUE, label: strings?.filterAll || '' }, ...options],
			value: selected || ALL_VALUE,
			onChange: (value: string) => navigate({ [id]: value === ALL_VALUE ? null : value, page: null }),
		}
	}

	// A taxonomy with nothing to choose between is noise, so only offer filters that
	// have at least one option across the full (unfiltered) result set.
	const heroFilters: SubpageHeroFilter[] = [
		{ id: 'industry', label: strings?.industryFilter || '', options: filters?.industries || [], selected: selectedFilters?.industry || null },
		{ id: 'offering', label: strings?.offeringFilter || '', options: filters?.offerings || [], selected: selectedFilters?.offering || null },
		{ id: 'location', label: strings?.locationFilter || '', options: filters?.locations || [], selected: selectedFilters?.location || null },
	]
		.filter((filter) => filter.options.length > 0)
		.map((filter) => buildFilter(filter.id, filter.label, filter.options, filter.selected))

	return (
		<>
			<ReferenceListPrototype
				overline={overline}
				heading={heading || ''}
				filters={heroFilters}
				cards={items.map(toCard).filter((card): card is Omit<CardProps, 'titleSize'> => card !== null)}
				currentPage={pagination?.currentPage || 1}
				totalPages={Math.max(pagination?.totalPages || 0, 1)}
				onPageChange={(page: number) => navigate({ page: page > 1 ? String(page) : null })}
				emptyMessage={strings?.emptyMessage}
			/>
			<Region name="main" data={component?.regions?.main?.components || []} meta={meta} />
		</>
	)
}
